import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../models/api-response.model';

export const SKIP_LOADER = new HttpContextToken<boolean>(() => false);
export const SKIP_ERROR = new HttpContextToken<boolean>(() => false);

interface HttpOptions {
  headers?: HttpHeaders;
  params?:
    | HttpParams
    | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> };
  skipLoader?: boolean;
  skipError?: boolean;
  context?: HttpContext;
  observe?: 'body';
  responseType?: 'json' | 'text' | 'arraybuffer';
  withCredentials?: boolean;
  reportProgress?: boolean;
}

interface HttpBlobOptions extends Omit<HttpOptions, 'responseType'> {
  responseType: 'blob';
}

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private http = inject(HttpClient);

  private buildContext(options?: HttpOptions | HttpBlobOptions): HttpContext {
    const context = options?.context ? options.context : new HttpContext();
    if (options?.skipLoader !== undefined) {
      context.set(SKIP_LOADER, options.skipLoader);
    }
    if (options?.skipError !== undefined) {
      context.set(SKIP_ERROR, options.skipError);
    }
    return context;
  }

  get<T>(url: string, options?: HttpOptions): Observable<ApiResponse<T>>;
  get(url: string, options: HttpBlobOptions): Observable<Blob>;
  get<T>(url: string, options?: HttpOptions | HttpBlobOptions): Observable<ApiResponse<T> | Blob> {
    const httpOptions = { ...options, context: this.buildContext(options) };
    if (options && 'responseType' in options && options.responseType === 'blob') {
      return this.http.get(
        url,
        httpOptions as Parameters<HttpClient['get']>[1]
      ) as Observable<Blob>;
    }
    return this.http.get<ApiResponse<T>>(url, httpOptions as Parameters<HttpClient['get']>[1]);
  }

  post<T>(url: string, body: unknown, options?: HttpOptions): Observable<ApiResponse<T>>;
  post(url: string, body: unknown, options: HttpBlobOptions): Observable<Blob>;
  post<T>(
    url: string,
    body: unknown,
    options?: HttpOptions | HttpBlobOptions
  ): Observable<ApiResponse<T> | Blob> {
    const httpOptions = { ...options, context: this.buildContext(options) };
    if (options && 'responseType' in options && options.responseType === 'blob') {
      return this.http.post(
        url,
        body,
        httpOptions as Parameters<HttpClient['post']>[2]
      ) as Observable<Blob>;
    }
    return this.http.post<ApiResponse<T>>(
      url,
      body,
      httpOptions as Parameters<HttpClient['post']>[2]
    );
  }

  put<T>(url: string, body: unknown, options?: HttpOptions): Observable<ApiResponse<T>>;
  put(url: string, body: unknown, options: HttpBlobOptions): Observable<Blob>;
  put<T>(
    url: string,
    body: unknown,
    options?: HttpOptions | HttpBlobOptions
  ): Observable<ApiResponse<T> | Blob> {
    const httpOptions = { ...options, context: this.buildContext(options) };
    if (options && 'responseType' in options && options.responseType === 'blob') {
      return this.http.put(
        url,
        body,
        httpOptions as Parameters<HttpClient['put']>[2]
      ) as Observable<Blob>;
    }
    return this.http.put<ApiResponse<T>>(
      url,
      body,
      httpOptions as Parameters<HttpClient['put']>[2]
    );
  }

  delete<T>(url: string, options?: HttpOptions): Observable<ApiResponse<T>>;
  delete(url: string, options: HttpBlobOptions): Observable<Blob>;
  delete<T>(
    url: string,
    options?: HttpOptions | HttpBlobOptions
  ): Observable<ApiResponse<T> | Blob> {
    const httpOptions = { ...options, context: this.buildContext(options) };
    if (options && 'responseType' in options && options.responseType === 'blob') {
      return this.http.delete(
        url,
        httpOptions as Parameters<HttpClient['delete']>[1]
      ) as Observable<Blob>;
    }
    return this.http.delete<ApiResponse<T>>(
      url,
      httpOptions as Parameters<HttpClient['delete']>[1]
    );
  }

  patch<T>(url: string, body: unknown, options?: HttpOptions): Observable<ApiResponse<T>>;
  patch(url: string, body: unknown, options: HttpBlobOptions): Observable<Blob>;
  patch<T>(
    url: string,
    body: unknown,
    options?: HttpOptions | HttpBlobOptions
  ): Observable<ApiResponse<T> | Blob> {
    const httpOptions = { ...options, context: this.buildContext(options) };
    if (options && 'responseType' in options && options.responseType === 'blob') {
      return this.http.patch(
        url,
        body,
        httpOptions as Parameters<HttpClient['patch']>[2]
      ) as Observable<Blob>;
    }
    return this.http.patch<ApiResponse<T>>(
      url,
      body,
      httpOptions as Parameters<HttpClient['patch']>[2]
    );
  }
}
